import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import {
  FitnessCenter,
  Check,
  ChevronRight,
} from '@mui/icons-material';
import { Exercise } from '../types/workout';

/**
 * WorkoutItem Component
 * 
 * A reusable component for displaying individual workout exercises in a list.
 * Features:
 * - Exercise name and details (sets, reps)
 * - Completion status indicator
 * - Click navigation
 * - Hover effects
 * - Consistent styling with gradient icon
 * 
 * Usage:
 * ```tsx
 * <WorkoutItem
 *   exercise={exercise}
 *   index={0}
 *   isCompleted={true}
 *   onClick={() => navigate('/exercise/0')}
 * />
 * ```
 */

export interface WorkoutItemProps {
  /** The exercise data to display */
  exercise: Exercise;
  /** The index of the exercise in the workout */
  index: number;
  /** Whether the exercise has been completed */
  isCompleted: boolean;
  /** Callback function when the item is clicked */
  onClick: () => void;
}

export const WorkoutItem: React.FC<WorkoutItemProps> = ({
  exercise,
  index,
  isCompleted,
  onClick
}) => {
  // Calculate total sets (warmup + working sets)
  const warmupSets = exercise.warmup_sets === '0' ? 0 : parseInt(exercise.warmup_sets.split('-').pop() || exercise.warmup_sets);
  const workingSets = parseInt(exercise.working_sets.split('-').pop() || exercise.working_sets);
  const totalSets = warmupSets + workingSets;

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={isCompleted ? <Check /> : <FitnessCenter />}
              label=""
              sx={{ 
                width: 56, 
                height: 56,
                background: isCompleted 
                  ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '& .MuiChip-label': { display: 'none' },
                '& .MuiChip-icon': { fontSize: 28, margin: 0, color: 'white' }
              }}
            />
            <Box>
              <Typography variant="body1" component="h3" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                {exercise.exercise}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {warmupSets} warmup • {workingSets} working
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}; 